import React, { useEffect, useState } from 'react';
import { leaveAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const LEAVE_TYPES = {
  annual: { label: '🌴 Phép năm', color: '#1e3a8a' },
  sick: { label: '🤒 Ốm', color: '#ef4444' },
  maternity: { label: '👶 Thai sản', color: '#ec4899' },
  paternity: { label: '👨‍👩‍👧 Vợ sinh', color: '#8b5cf6' },
  marriage: { label: '💍 Kết hôn', color: '#f59e0b' },
  funeral: { label: '🕊️ Tang lễ', color: '#6b7280' },
  unpaid: { label: '📋 Không lương', color: '#64748b' },
  compensatory: { label: '🔄 Bù phép', color: '#0891b2' },
};

const STATUS_CONFIG = {
  pending: { bg: '#fef9c3', color: '#ca8a04', label: 'Chờ duyệt' },
  approved: { bg: '#dcfce7', color: '#16a34a', label: 'Đã duyệt' },
  rejected: { bg: '#fee2e2', color: '#dc2626', label: 'Từ chối' },
  cancelled: { bg: '#f1f5f9', color: '#64748b', label: 'Đã hủy' },
};

export default function LeavePage() {
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({ leave_type: 'annual', from_date: '', to_date: '', reason: '' });
  const { user } = useAuth();

  const isManager = ['admin','hr_manager','manager'].includes(user?.role);

  const load = () => {
    setLoading(true);
    const params = {};
    if (user?.role === 'employee') params.employee_id = user.employee_id;
    Promise.all([
      leaveAPI.list(params),
      leaveAPI.balance(user?.employee_id, new Date().getFullYear()),
    ]).then(([reqRes, balRes]) => {
      setRequests(reqRes.data.data);
      setBalance(balRes.data.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const countWorkdays = (from, to) => {
    if (!from || !to) return 0;
    let days = 0;
    const cur = new Date(from);
    const end = new Date(to);
    while (cur <= end) {
      const d = cur.getDay();
      if (d !== 0 && d !== 6) days++;
      cur.setDate(cur.getDate() + 1);
    }
    return days;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (form.from_date && form.to_date && form.from_date > form.to_date) {
      setFormError('Ngày bắt đầu không được sau ngày kết thúc');
      return;
    }
    setSubmitting(true);
    try {
      await leaveAPI.create(form);
      setShowForm(false);
      setForm({ leave_type: 'annual', from_date: '', to_date: '', reason: '' });
      load();
    } catch (err) {
      setFormError(err.response?.data?.message || 'Lỗi tạo đơn');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAction = async (id, status) => {
    try {
      await leaveAPI.action(id, status);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Lỗi xử lý đơn');
    }
  };

  const annualBal = balance.find(b => b.leave_type === 'annual');

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>📅 Quản lý Nghỉ phép</h1>
        <button onClick={() => setShowForm(true)} style={{
          padding: '10px 20px', background: 'linear-gradient(135deg,#1e3a8a,#0891b2)',
          color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14
        }}>+ Tạo đơn nghỉ phép</button>
      </div>

      {/* Balance card */}
      {annualBal && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', display: 'flex', gap: 32, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Số dư phép năm {new Date().getFullYear()}</div>
            <div style={{ display: 'flex', gap: 24 }}>
              {[
                ['Tổng cộng', annualBal.total_days, '#1e3a8a'],
                ['Đã dùng', annualBal.used_days, '#ef4444'],
                ['Đang chờ', annualBal.pending_days, '#f59e0b'],
                ['Còn lại', annualBal.total_days - annualBal.used_days - annualBal.pending_days, '#22c55e'],
              ].map(([l, v, c]) => (
                <div key={l} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 26, fontWeight: 800, color: c }}>{v}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ background: '#f1f5f9', borderRadius: 6, height: 10, overflow: 'hidden' }}>
              <div style={{ width: `${(annualBal.used_days / annualBal.total_days * 100)}%`, background: '#ef4444', height: '100%' }} />
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              Đã dùng {annualBal.used_days}/{annualBal.total_days} ngày
            </div>
          </div>
        </div>
      )}

      {/* Requests table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Nhân viên', 'Loại nghỉ', 'Từ ngày', 'Đến ngày', 'Số ngày', 'Lý do', 'Trạng thái', isManager ? 'Hành động' : ''].filter(Boolean).map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>⏳ Đang tải...</td></tr>
            ) : requests.map(req => {
              const st = STATUS_CONFIG[req.status];
              const lt = LEAVE_TYPES[req.leave_type];
              return (
                <tr key={req.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ fontWeight: 500 }}>{req.employee_name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{req.department_name}</div>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ fontSize: 12, color: lt?.color, fontWeight: 500 }}>{lt?.label}</span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>{req.from_date?.substring(0,10)}</td>
                  <td style={{ padding: '12px 14px' }}>{req.to_date?.substring(0,10)}</td>
                  <td style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 700, color: '#1e3a8a' }}>{req.days}</td>
                  <td style={{ padding: '12px 14px', color: '#64748b', maxWidth: 200 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{req.reason}</div>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ background: st?.bg, color: st?.color, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 }}>{st?.label}</span>
                  </td>
                  {isManager && (
                    <td style={{ padding: '12px 14px' }}>
                      {req.status === 'pending' && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleAction(req.id, 'approved')}
                            style={{ padding: '5px 12px', background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            ✓ Duyệt
                          </button>
                          <button onClick={() => handleAction(req.id, 'rejected')}
                            style={{ padding: '5px 12px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                            ✗ Từ chối
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 480 }}>
            <h3 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700 }}>Tạo đơn nghỉ phép</h3>
            <form onSubmit={handleSubmit}>
              {formError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', color: '#dc2626', fontSize: 13, marginBottom: 16 }}>
                  ⚠️ {formError}
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Loại nghỉ phép</label>
                <select value={form.leave_type} onChange={e => setForm({...form, leave_type: e.target.value})}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14 }}>
                  {Object.entries(LEAVE_TYPES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Từ ngày</label>
                  <input type="date" required value={form.from_date}
                    onChange={e => { setFormError(''); setForm({...form, from_date: e.target.value}); }}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${formError && form.from_date > form.to_date && form.to_date ? '#fca5a5' : '#d1d5db'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Đến ngày</label>
                  <input type="date" required value={form.to_date} min={form.from_date}
                    onChange={e => { setFormError(''); setForm({...form, to_date: e.target.value}); }}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${formError && form.from_date > form.to_date && form.from_date ? '#fca5a5' : '#d1d5db'}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
              </div>
              {form.from_date && form.to_date && form.from_date <= form.to_date && (
                <div style={{ fontSize: 12, color: '#1d4ed8', marginBottom: 16, padding: '6px 10px', background: '#eff6ff', borderRadius: 6 }}>
                  📅 Số ngày làm việc: <strong>{countWorkdays(form.from_date, form.to_date)} ngày</strong>
                </div>
              )}
              <div style={{ marginBottom: 20, marginTop: form.from_date && form.to_date ? 0 : 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Lý do</label>
                <textarea rows={3} value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => { setShowForm(false); setFormError(''); }}
                  style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff' }}>
                  Hủy
                </button>
                <button type="submit" disabled={submitting}
                  style={{ padding: '10px 24px', background: submitting ? '#93c5fd' : '#1e3a8a', color: '#fff', border: 'none', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                  {submitting ? '⏳ Đang gửi...' : 'Gửi đơn'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
